
import { toLocalDate } from '@/utils/date';
import TextInput from '../text-input';
import Icon from '../../../../icon';
import { ControlEventType } from '../../enums';
import { TextInputProperty, TextInputType } from '../text-input/enums';
import { InputFillType, InputSlotType } from '../../shared/input/enums';
import { DatePickerProperty } from './enums';
import DatePickerProps from './mixins/DatePickerProps';
import { IconType, IconProperty } from '../../../../icon/enums';
import { StyleSizeType } from '../../../../../styles/enums';
import StyleClassNamesBuilder from '../../../../../styles/helpers/StyleClassNamesBuilder';
import {
  DateOptionsBuilder,
  DateFormatHelper,
  DayFormats,
  MonthFormats,
  YearFormats,
  WEEKDAY_INDEXES,
} from '../../../../../helpers/DateFormatHelper';

const DATEPICKER_REF = 'vuejs-datepicker';
const TEXT_INPUT_REF = 'root-ref';

const DateFormat = new DateOptionsBuilder()
  .addDayFormat(DayFormats.Numeric)
  .addMonthFormat(MonthFormats.Long)
  .addYearFormat(YearFormats.Numeric)
  .build();

export default {
  name: 'DatePicker',
  components: {
    Icon,
    TextInput,
  },
  mixins: [DatePickerProps],
  data() {
    return {
      isFocused: false,
      TEXT_INPUT_REF,
      DATEPICKER_REF,
      DatePickerProperty,
      TextInputProperty,
      TextInputType,
      IconType,
      IconProperty,
      InputSlotType,
      StyleSizeType,
      ControlEventType,
      currentYear: new Date().getFullYear(),
    };
  },
  computed: {
    excludedHolidayDates() {
      return this[DatePickerProperty.Holidays].reduce((acc, { dates }) => (
        [...acc, ...this.createHolidayDates(dates)]
      ), []);
    },
    datePickerClassNames() {
      return new StyleClassNamesBuilder('date-picker')
        .addColorSchemeConditionClassNames(this[DatePickerProperty.ColorScheme])
        .addConditionClassNames(Object.values(InputFillType), this[DatePickerProperty.FillType])
        .addConditionClassName('focused', this.isFocused)
        .build();
    },
    inputValue() {
      const currentValueDate = this[DatePickerProperty.Value];

      if (!currentValueDate) {
        return '';
      }

      return DateFormatHelper.format(toLocalDate(currentValueDate), DateFormat);
    },
    weekdayIndexes() {
      return this[DatePickerProperty.DisabledWeekdays].map((weekday) => WEEKDAY_INDEXES[weekday]);
    },
    disabledConfig() {
      const [disabledFrom, disabledTo] = this[DatePickerProperty.DisabledDateRange] || [];
      const minDate = this[DatePickerProperty.MinDate]
        && new Date(this[DatePickerProperty.MinDate]);
      const maxDate = this[DatePickerProperty.MaxDate]
        && new Date(this[DatePickerProperty.MaxDate]);

      return {
        to: minDate,
        from: maxDate,
        days: this.weekdayIndexes,
        ranges: [{
          from: disabledFrom,
          to: disabledTo,
        }],
        dates: this.excludedHolidayDates,
      };
    },
  },
  methods: {
    createHolidayDates(dates) {
      return dates.map(({ month, day }) => new Date(this.currentYear, month, day));
    },
    handleDatePickerChangeMonth(date) {
      const dateData = date.timestamp || date;
      this.currentYear = new Date(dateData).getFullYear();
    },
    handleDatePickerSelect(date) {
      this.$emit(ControlEventType.Change, date);
    },
    getInputRef() {
      return this.$refs[TEXT_INPUT_REF].$el.querySelector('input');
    },
    handleInputClick() {
      if (this[DatePickerProperty.Disabled]) {
        return;
      }

      if (!this.isFocused) {
        this.getInputRef()?.focus();
      }

      this.openPicker();
    },
    handleInputFocus() {
      this.isFocused = true;
      this.$emit(ControlEventType.Focus);
    },
    handleInputBlur() {
      this.isFocused = false;
      this.closePicker();
      this.$emit(ControlEventType.Blur);
    },
    openPicker() {
      this.$refs[DATEPICKER_REF]?.init();
      this.$refs[DATEPICKER_REF]?.showCalendar();
    },
    closePicker() {
      this.$refs[DATEPICKER_REF]?.close();
    },
  },
};
